<script lang="ts">
	import {
		SiteWideBanner,
		NavigationMenu,
		LandingHeader,
		Footer,
		ExitIntentNewsletterPopup
	} from "#lib/web-ui/home-buyers-template/content/index.js";
	import {
		setTemplateContext,
		cloudinaryUrl
	} from "#lib/web-ui/home-buyers-template/template-context.svelte.js";
	import { FaviconMeta } from "#lib/web-ui/meta/index.js";
	import type { LayoutProps } from "./$types";
	import { page } from "$app/state";
	import { afterNavigate } from "$app/navigation";
	import { PUBLIC_NODE_ENV } from "$app/env/public";
	import { googleTagManagerCode } from "#lib/web-ui/home-buyers-template/head-scripts.js";
	import { serializeScriptValue } from "@fergmar/ui/script";

	let { children, data }: LayoutProps = $props();

	const templateContext = setTemplateContext(() => data.siteConfig);
	const gtmDebug = $derived(page.url.searchParams.has("gtm_debug"));
	const validSocialUrls = $derived(
		Object.values(templateContext.socialUrls).filter((url): url is string => Boolean(url))
	);
	const logoImageUrl = $derived(cloudinaryUrl(templateContext.images.lightLogo, { width: 75 }));

	afterNavigate(({ shallow }) => {
		if (shallow) return;
		if (PUBLIC_NODE_ENV === "production") window.fbq?.("track", "PageView");
	});

	const organizationJsonLd = $derived({
		"@context": "https://schema.org",
		"@graph": [
			{
				"@type": "Organization",
				"@id": `${templateContext.siteUrl}#organization`,
				name: templateContext.siteName,
				url: templateContext.siteUrl,
				logo: {
					"@type": "ImageObject",
					url: logoImageUrl,
					width: 200,
					height: 75
				},
				address: {
					"@type": "PostalAddress",
					streetAddress: templateContext.contact.streetAddress,
					addressLocality: templateContext.contact.city,
					addressRegion: templateContext.contact.state,
					postalCode: templateContext.contact.zipCode,
					addressCountry: "US"
				},
				contactPoint: {
					"@type": "ContactPoint",
					telephone: templateContext.contact.phone,
					contactType: "customer service",
					email: templateContext.contact.email
				},
				...(validSocialUrls.length > 0 && { sameAs: validSocialUrls })
			},
			{
				"@type": "WebSite",
				"@id": `${templateContext.siteUrl}#website`,
				url: templateContext.siteUrl,
				name: templateContext.siteName,
				publisher: { "@id": `${templateContext.siteUrl}#organization` },
				inLanguage: "en-US"
			}
		]
	});
</script>

<svelte:head>
	<style>
		:root {
			--primary: hsl(32, 93%, 55%) !important;
		}

		.dark:root,
		.dark {
			--primary: hsl(32, 93%, 55%) !important;
		}
	</style>
	{#if PUBLIC_NODE_ENV === "production"}
		{#if gtmDebug}
			<svelte:element this={"script"}
				>{googleTagManagerCode(templateContext.googleAnalyticsId)}</svelte:element
			>
		{:else}
			<script>
				partytown = {
					forward: ["dataLayer.push", "gtag"],
					resolveUrl: function (url, location, type) {
						if (type === "script" && !url.href.includes("/api/partytown-proxy")) {
							const proxyUrl = new URL("https://app.fergmar.com/api/partytown-proxy");
							proxyUrl.searchParams.append("url", url.href);
							return proxyUrl;
						}
						return url;
					}
				};
			</script>
			<script src="/~partytown/partytown.js"></script>

			<svelte:element this={"script"} type="text/partytown"
				>{googleTagManagerCode(templateContext.googleAnalyticsId)}</svelte:element
			>
		{/if}
		<script>
			!(function (f, b, e, v, n, t, s) {
				if (f.fbq) return;
				n = f.fbq = function () {
					n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
				};
				if (!f._fbq) f._fbq = n;
				n.push = n;
				n.loaded = !0;
				n.version = "2.0";
				n.queue = [];
				t = b.createElement(e);
				t.async = !0;
				t.src = v;
				s = b.getElementsByTagName(e)[0];
				s.parentNode.insertBefore(t, s);
			})(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
			fbq("init", "25114358511596870");
		</script>
	{/if}

	<meta name="viewport" content="width=device-width, initial-scale=1" />
	<link rel="preconnect" href="https://www.google.com" />
	<link rel="preconnect" href="https://maps.googleapis.com" />
	<link rel="preconnect" href="https://maps.gstatic.com" />
	<link rel="canonical" href="{page.url.origin}{page.url.pathname}" />

	<FaviconMeta faviconSlug="fergmar-home-buyers" />
	<meta property="og:locale" content="en_US" />
	<meta property="og:site_name" content={templateContext.siteName} />

	<meta name="twitter:card" content="summary_large_image" />

	<svelte:element this={"script"} type="application/ld+json"
		>{serializeScriptValue(organizationJsonLd)}</svelte:element
	>
</svelte:head>

<div class="flex min-h-screen max-w-screen flex-col overflow-x-hidden">
	{#if PUBLIC_NODE_ENV === "production"}
		<noscript>
			<iframe
				src="https://www.googletagmanager.com/ns.html?id={templateContext.googleAnalyticsId}"
				height="0"
				width="0"
				style="display:none;visibility:hidden"
				title="Google Tag Manager"
			></iframe>
		</noscript>
		<noscript
			><img
				height="1"
				width="1"
				style="display:none"
				alt=""
				src="https://www.facebook.com/tr?id=25114358511596870&ev=PageView&noscript=1"
			/></noscript
		>
	{/if}
	{#if page.data.landing}
		<LandingHeader />
	{:else}
		<SiteWideBanner />
		<NavigationMenu />
		<ExitIntentNewsletterPopup listId="32fd5132-d47b-11f0-b6f5-02422ec4e037" />
	{/if}

	<main>
		{@render children?.()}
	</main>

	<Footer
		logoUrl={templateContext.images.darkLogo}
		name={templateContext.siteName}
		contact={templateContext.contact}
		bbbBadgeUrl={templateContext.images.bbbBadge}
		social={templateContext.socialUrls}
		servicedAreas={templateContext.servicedAreas}
	/>
</div>
